import React, { useContext } from 'react';
import { Typography, Link as MuiLink } from '@mui/material';
import {
  Business as BusinessIcon,
  Link as LinkIcon,
  LocationOn as LocationOnIcon,
  MonetizationOn as MonetizationOnIcon,
  Today as TodayIcon,
  Work as WorkIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import CardBase from 'components/CardBase';
import CreateProcessContext from 'shared/contexts/CreateProcessContext';
import { format as formatDate, parseISO } from 'date-fns';
import { getDisplayCompanyName } from 'shared/utilities';
import { Money } from 'API';
import IconRow from './IconRow';
import IconRowCell from './IconRowCell';
import CardHeader from './CardHeader';
import CurrencyDisplay from './Currency';
import InlineDownloadButton from './InlineDownloadButton';

const EmploymentInfoCard: React.FC = () => {
  const {
    process,
    selectedCompany,
    selectedJob,
    selectedNational,
    uploadedDescription,

  } = useContext(CreateProcessContext);

  // Company is going to come from selectedNational, if this is
  // an existing employee, or it’s going to come from selectedCompany,
  // if it’s a new employee.
  const company = selectedNational ? selectedNational.company : selectedCompany;

  let address = null;
  switch (process.employmentData?.location?.type) {
    case 'HEADQUARTERS':
      address = company?.data?.addressPrimary;
      break;
    case 'SECONDARY':
      address = company?.data?.addressSecondary;
      break;
    case 'REMOTE':
      address = { address: 'Working Remotely' };
      break;
    default:
      address = { address: 'Working Remotely' };
  }

  return (
    <CardBase>
      <CardHeader icon={BusinessIcon} title="Employment information" />

      <IconRow>
        <IconRowCell type="icon">
          <MonetizationOnIcon color="action" />
        </IconRowCell>
        <IconRowCell type="text">
          <Typography variant="body2">OFFERED SALARY</Typography>
          <Typography variant="caption">
            {(process.employmentData?.salary?.amount !== undefined
              && process.employmentData?.salary?.amount !== null)
              ? <CurrencyDisplay value={process.employmentData?.salary as Money} />
              : 'N/A'}
          </Typography>
        </IconRowCell>
      </IconRow>

      <IconRow>
        <IconRowCell type="icon">
          <WorkIcon color="action" />
        </IconRowCell>
        <IconRowCell type="text">
          <Typography variant="body2">JOB TITLE</Typography>
          <Typography variant="caption">{selectedJob?.title}</Typography>
        </IconRowCell>
      </IconRow>

      {selectedJob?.jobURL && (
        <IconRow>
          <IconRowCell type="icon">
            <LinkIcon color="action" />
          </IconRowCell>
          <IconRowCell type="text">
            <Typography variant="body2">JOB URL</Typography>
            <MuiLink href={selectedJob?.jobURL} target="_blank">{selectedJob?.jobURL}</MuiLink>
          </IconRowCell>
        </IconRow>
      )}

      <IconRow>
        <IconRowCell type="icon">
          <InsertDriveFileIcon color="action" />
        </IconRowCell>
        <IconRowCell type="text">
          <Typography variant="body2">JOB DESCRIPTION</Typography>

          {uploadedDescription
            ? (
              <Typography variant="caption" component="p" color="primary">
                <InlineDownloadButton
                  file={uploadedDescription}
                >
                  {uploadedDescription.fileName}
                </InlineDownloadButton>
              </Typography>
            )
            : (
              <Typography variant="caption" component="p">
                N/A
              </Typography>
            )}
        </IconRowCell>
      </IconRow>

      <IconRow>
        <IconRowCell type="icon">
          <LocationOnIcon color="action" />
        </IconRowCell>
        <IconRowCell type="text">
          <Typography variant="body2">PHYSICAL WORKING ADDRESS</Typography>
        </IconRowCell>
      </IconRow>
      <IconRow>
        <IconRowCell type="text">
          <Typography variant="caption">{getDisplayCompanyName(company?.displayCompanyName, company?.name)}</Typography>
          {address?.address && (
            <Typography variant="caption">{address?.address}</Typography>
          )}
          {address?.city && address?.adminArea && address?.postalCode && (
            <>
              <Typography variant="caption">
                {address?.city}
                {', '}
                {address?.adminArea}
                {' '}
                {address?.postalCode}
              </Typography>
            </>
          )}
        </IconRowCell>
      </IconRow>

      <IconRow>
        <IconRowCell type="icon">
          <TodayIcon color="action" />
        </IconRowCell>
        <IconRowCell type="text">
          <Typography variant="body2">EMPLOYMENT START DATE</Typography>
          <Typography variant="caption">
            {process.employmentData?.startDate
              ? formatDate(parseISO(process.employmentData.startDate), 'M/d/yy')
              : 'N/A'}
          </Typography>
        </IconRowCell>
      </IconRow>
    </CardBase>
  );
};

export default EmploymentInfoCard;
